import { useState } from "react";
import { Info } from "lucide-react";
import PolicyWordingModal, { FEATURE_POLICY_MAPPING, RIDER_POLICY_MAPPING } from "./PolicyWordingModal";

// Clickable feature wrapper component
const ClickableFeature = ({ 
  featureKey,
  value,
  planId,
  planName,
  planDocuments,
  children,
  className = "",
  isRider = false,
  customTitle,
  customDescription,
  showIcon = true,
  disabled = false
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Check if this feature has policy mapping
  const mapping = isRider ? RIDER_POLICY_MAPPING : FEATURE_POLICY_MAPPING;
  const hasMapping = featureKey && (mapping[featureKey] || customTitle);
  
  // If no mapping and not custom, just render children
  if (!hasMapping && !customTitle) {
    return <span className={className}>{children}</span>;
  }
  
  if (disabled) {
    return <span className={className}>{children}</span>;
  }
  
  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className={`inline-flex items-center gap-1 hover:text-[#A87608] transition-colors cursor-pointer group ${className}`}
        title="Click to view policy wording explanation"
      >
        <span className="border-b border-dashed border-current group-hover:border-amber-400">
          {children}
        </span>
        {showIcon && (
          <Info className="h-3 w-3 opacity-50 group-hover:opacity-100 flex-shrink-0" />
        )}
      </button>
      
      <PolicyWordingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        featureKey={featureKey}
        featureValue={value || (typeof children === 'string' ? children : '')}
        planId={planId}
        planName={planName}
        planDocuments={planDocuments}
        customTitle={customTitle}
        customDescription={customDescription}
        isRider={isRider}
      />
    </>
  );
};

// HOC to make any value clickable with policy wording
export const withPolicyWording = (Component) => {
  return function WrappedComponent({ 
    featureKey,
    planId,
    planName, 
    planDocuments, 
    isRider,
    ...props 
  }) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    return (
      <>
        <div 
          onClick={() => setIsModalOpen(true)}
          className="cursor-pointer hover:text-[#A87608] transition-colors"
        >
          <Component {...props} />
        </div>
        
        <PolicyWordingModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          featureKey={featureKey}
          planId={planId}
          planName={planName}
          planDocuments={planDocuments}
          isRider={isRider}
        />
      </>
    );
  };
};

// Utility to check if a feature key is clickable
export const isFeatureClickable = (featureKey, isRider = false) => {
  const mapping = isRider ? RIDER_POLICY_MAPPING : FEATURE_POLICY_MAPPING;
  return !!mapping[featureKey];
};

// Get all clickable feature keys
export const getClickableFeatures = () => Object.keys(FEATURE_POLICY_MAPPING);
export const getClickableRiders = () => Object.keys(RIDER_POLICY_MAPPING);

export default ClickableFeature;
